import './Targeting.scss';

import React, { useMemo } from 'react';

import type { TargetingRule } from '../../../apis/graphql';
import type { AdType } from '../../../configs';
import type { AdSequence, Nullable, TargetingTermValue } from '../../../models';
import {
  addPublisherToTargetingRule,
  filterOutCountryTarget,
} from '../../../pages/Trafficking/DrawerTargetingTable/utils/utils';
import { AlertFiled } from '../../../pages/Trafficking/TraffickingPage/cells/AlertCell';
import { EntityType } from '../../../pages/Trafficking/TraffickingPage/hooks/makePageDrawerPlugin';
import bem from '../../../utils/bem';
import { getCountriesTargetFromAdTargetingSubmission, getPublisherTargetLineItem } from '../../adTargeting';
import { useFieldArray, useFieldFast } from '../../Form/hooks';
import { TargetingPanel } from '../../Targeting';
import type {
  TargetingPanelAlerts,
  TargetingPanelHeader,
  TargetingPanelTextViewer,
} from '../../Targeting/TargetingPanel/TargetingPanel';
import type { TargetingTableRowGroup } from '../../TargetingTable/TargetingTable';
import { normalizeToArray } from '../../utils';

const [block, element] = bem('targeting');

export type TargetingProps = Readonly<{
  lineItemTargetingTermValues: TargetingTermValue[];
  publisher: string;
  lineItemCountryIds?: string[];
  readonly?: boolean;
  publisherIds?: string[];
  targetingRule?: TargetingRule;
  adRule?: TargetingRule;
  hasAdTermsChanged?: boolean;
  hasInvalidPublisher?: Nullable<boolean>;
  hasInvalidCountry?: Nullable<boolean>;
  adSequence?: Nullable<AdSequence>;
}>;

function Targeting({
  lineItemTargetingTermValues,
  publisher,
  lineItemCountryIds,
  readonly,
  publisherIds,
  targetingRule,
  adRule,
  hasAdTermsChanged,
  hasInvalidPublisher,
  hasInvalidCountry,
  adSequence,
}: TargetingProps): JSX.Element {
  const [
    ,
    { value: adTargetingTermValues, error, initialValue: initialAdTargetingTermValues },
    listHelpers,
  ] = useFieldArray<TargetingTermValue>('targetingTermValues');
  const [{ value: adType }] = useFieldFast<AdType>('type');

  const countryIdsForFilteringValues = useMemo(
    () => getCountriesTargetFromAdTargetingSubmission(initialAdTargetingTermValues ?? [], lineItemCountryIds),
    [initialAdTargetingTermValues, lineItemCountryIds]
  );

  const data: TargetingTableRowGroup[] = useMemo(
    (): TargetingTableRowGroup[] => [
      {
        groupType: 'Ad Targeting',
        targetingTermValues: adTargetingTermValues,
      },
      {
        groupType: 'Line Item Targeting',
        targetingTermValues: filterOutCountryTarget({ value: lineItemTargetingTermValues }) ?? [],
      },
    ],
    [adTargetingTermValues, lineItemTargetingTermValues]
  );

  const lineItemRuleWithPublisher = useMemo(() => {
    return addPublisherToTargetingRule(targetingRule, publisherIds);
  }, [targetingRule, publisherIds]);

  const lineItemPublishers = useMemo(() => getPublisherTargetLineItem(publisherIds || []) || undefined, [publisherIds]);

  const adTypes = useMemo(() => normalizeToArray(adType), [adType]);

  const headerConfig: TargetingPanelHeader = useMemo(
    () => ({
      countryLabel: 'Line Country: ',
      publisherLabel: 'Line Publisher: ',
      lineItemPublishers: publisher,
      lineItemCountryIds,
      hideViewToggleWhen: hasAdTermsChanged,
    }),
    [publisher, lineItemCountryIds, hasAdTermsChanged]
  );

  const alertsConfig: TargetingPanelAlerts = useMemo(
    () => ({
      hasInvalidPublisher,
      hasInvalidCountry,
      adSequence,
      classNames: {
        invalidPublisher: element('targeting-error-alert'),
        invalidCountry: element(AlertFiled.InvalidCountry + '-alert'),
        sequenceInfo: element('sequence-info-alert'),
      },
    }),
    [hasInvalidPublisher, hasInvalidCountry, adSequence]
  );

  const textViewerConfig: TargetingPanelTextViewer = useMemo(
    () => ({
      entityType: EntityType.AD,
      adRule,
      lineItemRule: lineItemRuleWithPublisher,
      // AdForm intentionally does NOT force-table-view when hasAdTermsChanged
      // flips; the View toggle button is hidden via `header.hideViewToggleWhen`
      // but a previously-chosen text view persists. See TargetingPanel
      // tests "AdForm contract".
      className: element('text-view-wrapper'),
    }),
    [adRule, lineItemRuleWithPublisher]
  );

  return (
    <div className={block()}>
      <TargetingPanel
        data={data}
        listHelpers={listHelpers}
        editMode={!readonly}
        isAd
        isTableToggleable
        editingEntityType="ad"
        header={headerConfig}
        alerts={alertsConfig}
        textViewer={textViewerConfig}
        dropdowns={{
          scope: {
            isAdEntity: true,
            lineItemCountryIds,
            countryIdsForFilteringValues,
            lineItemPublishers,
            publishersForFilteringValues: lineItemPublishers,
            adTypes,
            allowEditCountryTarget: !!hasInvalidCountry || (lineItemCountryIds?.length ?? 0) > 1,
          },
          targetings: adTargetingTermValues,
          listHelpers,
          validationError: error,
          readonly,
        }}
        search={{ placeholder: 'Search Targeted Values', className: element('targeting-filter') }}
      />
    </div>
  );
}

export default Targeting;
